import React, { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, User2 } from "lucide-react";
import PublicNav from "@/components/landing/PublicNav";
import Footer from "@/components/landing/Footer";
import AdSlot from "@/components/AdSlot";
import NewsletterForm from "@/components/NewsletterForm";
import { useSeo } from "@/hooks/useSeo";
import { ARTICLES, findArticle } from "@/content/blogArticles";

// Minimal markdown-ish → JSX renderer. Handles: # ## ###, **bold**, [link](url),
// bullet lists, tables (pipe-delimited), and paragraphs. Ad slot injected after
// the 2nd top-level block.
function renderBody(body) {
    const lines = body.trim().split("\n");
    const blocks = [];
    let buf = [];
    let inTable = false;
    let tableRows = [];
    let inList = false;
    let listItems = [];

    const flushPara = () => {
        if (buf.length) {
            blocks.push({ type: "p", content: buf.join(" ") });
            buf = [];
        }
    };
    const flushList = () => {
        if (listItems.length) {
            blocks.push({ type: "ul", items: listItems });
            listItems = [];
        }
        inList = false;
    };
    const flushTable = () => {
        if (tableRows.length) blocks.push({ type: "table", rows: tableRows });
        tableRows = [];
        inTable = false;
    };

    for (const raw of lines) {
        const line = raw.trim();
        if (!line) { flushPara(); flushList(); flushTable(); continue; }
        if (line.startsWith("## ")) { flushPara(); flushList(); flushTable(); blocks.push({ type: "h2", content: line.slice(3) }); continue; }
        if (line.startsWith("### ")) { flushPara(); flushList(); flushTable(); blocks.push({ type: "h3", content: line.slice(4) }); continue; }
        if (line.startsWith("- ")) {
            flushPara(); flushTable();
            inList = true;
            listItems.push(line.slice(2));
            continue;
        }
        if (line.startsWith("|")) {
            flushPara(); flushList();
            const cells = line.split("|").slice(1, -1).map(c => c.trim());
            if (cells.every(c => /^-+$/.test(c))) continue; // skip separator row
            inTable = true;
            tableRows.push(cells);
            continue;
        }
        if (inList) flushList();
        if (inTable) flushTable();
        buf.push(line);
    }
    flushPara(); flushList(); flushTable();
    return blocks;
}

// Render inline markdown: **bold**, `code`, [text](url).
function inline(text) {
    if (!text) return text;
    const parts = [];
    let remaining = text;
    let key = 0;
    while (remaining.length) {
        // bold
        let m = remaining.match(/^(.*?)\*\*(.+?)\*\*(.*)$/);
        if (m) {
            if (m[1]) parts.push(<span key={key++}>{m[1]}</span>);
            parts.push(<strong key={key++} className="text-white font-semibold">{m[2]}</strong>);
            remaining = m[3];
            continue;
        }
        // link
        m = remaining.match(/^(.*?)\[([^\]]+)\]\(([^)]+)\)(.*)$/);
        if (m) {
            if (m[1]) parts.push(<span key={key++}>{m[1]}</span>);
            parts.push(<a key={key++} href={m[3]} target="_blank" rel="noreferrer" className="text-brand-cyan hover:underline">{m[2]}</a>);
            remaining = m[4];
            continue;
        }
        // inline code
        m = remaining.match(/^(.*?)`([^`]+)`(.*)$/);
        if (m) {
            if (m[1]) parts.push(<span key={key++}>{m[1]}</span>);
            parts.push(<code key={key++} className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/8 font-mono text-xs text-brand-cyan">{m[2]}</code>);
            remaining = m[3];
            continue;
        }
        parts.push(<span key={key++}>{remaining}</span>);
        remaining = "";
    }
    return parts;
}

export default function BlogArticle() {
    const { slug } = useParams();
    const article = findArticle(slug);
    const canonical = useMemo(() => (typeof window !== "undefined" ? `${window.location.origin}/blog/${slug}` : ""), [slug]);
    const jsonLd = useMemo(() => article ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": article.title,
        "description": article.description,
        "datePublished": article.published_at,
        "author": { "@type": "Organization", "name": article.author },
        "publisher": { "@type": "Organization", "name": "BOXEB2B", "url": "https://boxe-b2b.com" },
        "keywords": article.keywords,
        "url": canonical,
    } : null, [article, canonical]);

    useSeo({
        title: article ? `${article.title} — BOXEB2B` : "Article introuvable — BOXEB2B",
        description: article?.description,
        keywords: article?.keywords,
        canonical,
        jsonLd,
    });

    if (!article) return <Navigate to="/blog" replace />;

    const blocks = renderBody(article.body);
    // Split into halves for mid-article ad insertion
    const midIndex = Math.min(4, Math.floor(blocks.length / 2));

    return (
        <div className="relative min-h-screen bg-brand-void text-white">
            <PublicNav />

            <article className="pt-28 pb-16 px-6 lg:px-10">
                <div className="mx-auto max-w-3xl">
                    <Link
                        to="/blog"
                        data-testid="blog-back"
                        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-brand-cyan transition mb-8"
                    >
                        <ArrowLeft size={14} /> Retour au blog
                    </Link>

                    <div className={`h-52 rounded-2xl bg-gradient-to-br ${article.cover_gradient} flex items-center justify-center mb-8 relative overflow-hidden`}>
                        <div className="absolute inset-0 bg-grain opacity-30" />
                        <span className="relative text-8xl">{article.emoji}</span>
                    </div>

                    <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.1]">
                        {article.title}
                    </h1>

                    <div className="mt-5 flex items-center gap-4 text-xs font-mono text-zinc-500">
                        <span className="inline-flex items-center gap-1.5"><Calendar size={12} />{article.published_at}</span>
                        <span className="inline-flex items-center gap-1.5"><Clock size={12} />{article.read_min} min lecture</span>
                        <span className="inline-flex items-center gap-1.5"><User2 size={12} />{article.author}</span>
                    </div>

                    <div className="mt-8 prose-content text-zinc-300 leading-relaxed">
                        {blocks.map((b, i) => (
                            <React.Fragment key={i}>
                                {i === midIndex && <AdSlot slot="2222222222" format="fluid" layout="in-article" testId="blog-inarticle-ad" />}
                                {b.type === "h2" && <h2 className="font-display font-black text-2xl tracking-tighter text-white mt-10 mb-4">{inline(b.content)}</h2>}
                                {b.type === "h3" && <h3 className="font-display font-bold text-lg tracking-tight text-white mt-6 mb-3">{inline(b.content)}</h3>}
                                {b.type === "p" && <p className="mb-4 text-base">{inline(b.content)}</p>}
                                {b.type === "ul" && (
                                    <ul className="mb-4 space-y-2 pl-5 list-disc marker:text-brand-cyan">
                                        {b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}
                                    </ul>
                                )}
                                {b.type === "table" && (
                                    <div className="my-6 overflow-x-auto rounded-xl border border-white/10">
                                        <table className="w-full text-sm">
                                            <thead className="bg-white/[0.04] text-xs uppercase tracking-widest text-zinc-500">
                                                <tr>{b.rows[0].map((c, k) => <th key={k} className="p-3 text-left">{inline(c)}</th>)}</tr>
                                            </thead>
                                            <tbody>
                                                {b.rows.slice(1).map((r, ri) => (
                                                    <tr key={ri} className="border-t border-white/8">
                                                        {r.map((c, ci) => <td key={ci} className="p-3 text-zinc-300">{inline(c)}</td>)}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    <AdSlot slot="3333333333" format="auto" testId="blog-bottom-ad" className="mt-12" />

                    {/* Newsletter capture — the real conversion tool */}
                    <div className="mt-10">
                        <NewsletterForm source={`blog-${article.slug}`} />
                    </div>

                    <div className="mt-8 rounded-2xl p-6 bg-gradient-to-br from-brand-indigo/20 to-brand-cyan/10 border border-brand-indigo/30">
                        <div className="font-display font-bold text-xl tracking-tight">Testé, adopté, sans engagement.</div>
                        <p className="mt-2 text-sm text-zinc-300">Applique ce guide dans BOXEB2B avec 50 crédits offerts. Aucune carte demandée.</p>
                        <Link
                            to="/register"
                            data-testid="blog-cta-signup"
                            className="mt-4 inline-flex btn-cta text-white font-semibold text-sm px-5 py-2.5 rounded-full"
                        >
                            Commencer gratuitement →
                        </Link>
                    </div>
                </div>
            </article>

            <Footer />
        </div>
    );
}
